import { BarChart3, Boxes, Calculator, EyeOff, FileText, History, Images, ListOrdered, Package, PackageCheck, Puzzle, Receipt } from "lucide-react";
import { useMemo, useState } from "react";
import { LeaveGuard } from "../../web/components/LeaveGuard";
import { useConfirm, useWindowConfirm } from "../../web/components/Confirm";
import { RecordWindow } from "../../web/components/RecordWindow";
import { archiveAction } from "../../web/components/recordActions";
import { FixHere } from "../../web/components/FixHere";
import { ErrorNotice, IssueChip, Issues, Notice } from "../../web/components/ui";
import { useCompany } from "../../web/lib/company";
import { nextShowToken } from "../../web/lib/flash";
import { useFreshRecord } from "../../web/lib/queries";
import { plural } from "../../shared/words";
import { useNewUuid } from "../../web/lib/newUuid";
import { can, itemName, usePriceListSaver, whyNot } from "./data";
import { inUse, isProduct, type ItemState, itemState, type MaterialRow, type PriceListAction, type PriceListData } from "./meta";
import { SmartNameNotice, TagClashNotice } from "./NameTags";
import { isLabour } from "./pricing";
import { duplicatesOf } from "./setup";
import { CheckNotice, PricingTab, profitTitle } from "./PricingTab";
import { ItemStatsTab, withStatsTab } from "./StatsTab";
import { CostUpdateNotice, SupplierPricesTab } from "./SupplierPricesTab";
import { usePriceListWindows, useUnsavedGuard } from "./windows";
import { ChangedNotice, HistoryTab } from "./HistoryTab";
import { type ItemProblemId, itemContext, itemProblems, pricesDueDetail, sharedNameText, sharedNumberText } from "./attention";
import { DuplicatePopup, duplicateAction } from "./DuplicatePopup";
import { MergePopup } from "./MergePopup";
import { BuildPopup, PartsTab } from "./PackageCards";
import { CheckedPill } from "./CheckedMark";
import { PhotosTab, usePhotos } from "./PhotosTab";
import { DetailsTab } from "./ItemDetailsTab";
import { freeNumber, stateActions } from "./itemActions";
import { useItemDrafts } from "./itemWindowDrafts";
import { builtItemProblems } from "./UsedIn";

/*
 * One material, in a window like ServiceM8's job card: an item (in ServiceM8: plain, or built from products, its Contents) or a
 * product (catalogue.ts: its pricing, cost and supplier prices; in a window of its own). Details, Pricing, Cost (and suppliers), Stats and History tabs,
 * with Archive / Restore (and Remove from / Add to ServiceM8) in its More menu, each asked first. Fields save as you leave them, to ServiceM8 as you, except
 * its own pricing: a draft until it's saved, like a group's (kept here, so it stays while another tab
 * is open, marked on the Pricing tab; closing the window asks first, in the window).
 * Opening it fetches the item from ServiceM8, so it shows exactly what's there now.
 */

const TABS = [
    { id: "details", label: "Details", icon: <FileText className="size-5" /> },
    // A built item's (packages.ts): the products it's built from, which price it (as a bundle's Contents).
    { id: "contents", label: "Contents", icon: <ListOrdered className="size-5" /> },
    { id: "pricing", label: "Pricing", icon: <Calculator className="size-5" /> },
    // Its cost (id kept from when it was its supplier prices too: links and windows open it by it).
    { id: "suppliers", label: "Costing", icon: <Receipt className="size-5" /> },
    // What it's costed from: its supplier prices.
    { id: "stats", label: "Stats", icon: <BarChart3 className="size-5" /> },
    // A product's (photos.ts): to show a customer.
    { id: "photos", label: "Photos", icon: <Images className="size-5" /> },
    { id: "history", label: "History", icon: <History className="size-5" /> },
];

/** What's changed on an item's tabs: refused while it's archived (seeing, stats, restoring and reviewing changes aren't). */
const ARCHIVED_LOCKS: readonly PriceListAction[] = ["items.edit", "items.pricing", "costs.edit", "groups", "prices.add", "prices.update", "suppliers.edit"];
const ARCHIVED = "It's archived: restore it to make changes.";

/** The data as an archived item's tabs see it: every change on them refused (a reason already there stays). */
function readOnlyData(data: PriceListData): PriceListData {
    const whyNot = { ...data.whyNot };
    for (const action of ARCHIVED_LOCKS) whyNot[action] ??= ARCHIVED;
    return { ...data, whyNot };
}

/** Tabs about what the business pays: only with See costs (the server leaves it out otherwise). */
const COST_TABS: ReadonlySet<string> = new Set(["pricing", "suppliers"]);

/**
 * The tabs this person can see: Stats with See stats (an item's: products aren't on jobs themselves); Pricing and Costing with See
 * costs; Contents, a built item's (as a bundle's); Photos, a product's.
 */
const itemTabs = (data: PriceListData, item: MaterialRow | undefined, isPackage: boolean) =>
    withStatsTab(TABS, data).filter(
        (t) =>
            (!COST_TABS.has(t.id) || can(data, "costs.view")) &&
            (t.id !== "contents" || isPackage) &&
            (t.id !== "stats" || !item || !isProduct(item)) &&
            (t.id !== "photos" || (item !== undefined && isProduct(item))),
    );

/** What each tab needs to change things on it (the window says why when it can't). */
const TAB_ACTIONS: Record<string, PriceListAction> = { details: "items.edit", contents: "items.pricing", pricing: "items.pricing", suppliers: "costs.edit" };
/** What its tab's changes need. */
const tabAction = (tab: string): PriceListAction => TAB_ACTIONS[tab] ?? "items.edit";

/** Fields a warning at the top can take you to, and the tab each is on. */
const FOCUS_TABS = { itemNumber: "details", name: "details", cost: "suppliers" } as const;
type FocusField = keyof typeof FOCUS_TABS;

/** Where a warning at the top is put right (FixHere: the whole warning, with its buttons), and the tab each is on. */
const FIX_TABS = { pricing: "pricing", profit: "pricing", cost: "suppliers", prices: "suppliers", name: "details", usedIn: "details", history: "history" } as const;
type FixSpot = keyof typeof FIX_TABS;

export function ItemWindow(
    props: Readonly<{
        data: PriceListData;
        uuid: string;
        tab: string;
        onTab: (tab: string) => void;
        onClose: () => void;
        /** Which window it's in (windows.ts): a product's has its own. */
        kind: "item" | "product";
    }>,
) {
    const company = useCompany();
    useFreshRecord(company.id, "material", props.uuid);
    const item = props.data.materials.find((m) => m.uuid === props.uuid);
    // Built from products (packages.ts): sold as one line, priced as they add up, its Contents laid out as a bundle's.
    const isPackage = item !== undefined && item.uuid in props.data.packages;
    // A product (catalogue.ts): kept only here, what items are built from.
    const product = item !== undefined && isProduct(item);
    // A product's photos, counted on its Photos tab.
    const photoCount = usePhotos("material", props.uuid, product).query.data?.length;
    const word = product ? "product" : "item";
    const kind = props.kind;
    // Retired, its tabs are read-only (restored, they can be changed): every change on them is refused, saying why. Hidden, it's in use.
    const retired = item !== undefined && !inUse(item);
    const tabData = useMemo(() => (retired ? readOnlyData(props.data) : props.data), [props.data, retired]);
    const saver = usePriceListSaver();
    const [archiveError, setArchiveError] = useState<Error | null>(null);
    // Duplicating it: its popup open (DuplicatePopup).
    const [duplicating, setDuplicating] = useState(false);
    // A copy or bundle made from it: its uuid, the same for every try (a retry after a timeout can't make a second).
    const newUuid = useNewUuid();
    // Merging it with a duplicate of it (MergePopup): its popup open.
    const [merging, setMerging] = useState(false);
    // Building it from products (BuildPopup): its popup open.
    const [packaging, setPackaging] = useState(false);
    // Making a bundle of it (DuplicatePopup, asking the bundle's name and item number): its popup open.
    const [bundling, setBundling] = useState(false);
    // A product sold as an item (DuplicatePopup, asking the item's name and item number): its popup open.
    const [selling, setSelling] = useState(false);
    const windows = usePriceListWindows();
    // Asking about unsaved changes before leaving them (closing, or opening another item here): a popup in the window (its `Here` in it).
    const windowConfirm = useWindowConfirm();
    // Its name tags being changed, not saved yet (its Details tab): kept while other tabs are open.
    // Bumped each time it asks, so the Pricing tab scrolls its draft into view.
    const [reveal, setReveal] = useState(0);
    // A warning's button (Add item number, Add cost) takes you to the field it's about, on its tab, every time it's pressed.
    const [focus, setFocus] = useState<{ field: FocusField; token: number } | null>(null);
    const goTo = (field: FocusField) => {
        props.onTab(FOCUS_TABS[field]);
        setFocus({ field, token: nextShowToken() });
    };
    const focusOn = (field: FocusField) => (focus?.field === field ? focus.token : undefined);
    // A warning's Show takes you to where it's put right, on its tab, every time it's pressed.
    const [fix, setFix] = useState<{ spot: FixSpot; token: number } | null>(null);
    const fixToken = (spot: FixSpot) => (fix?.spot === spot ? fix.token : undefined);
    // Its drafts (itemWindowDrafts.ts: pricing, cost, products, details, tags, the supplier price being added), kept while other tabs
    // are open; another item opened here starts them afresh (and where a warning's Show took you).
    const { draft, setDraft, details, setDetailsDraft, tagsDraft, setTagsDraft, priceDraft, setPriceDraft, costDraft, setCostDraft, partsDraft, setPartsDraft, edited, unsaved, left, unsavedNow, askTab, tabUnsaved } =
        useItemDrafts(props.uuid, props.tab, props.data, item, product, () => setFix(null));
    /**
     * Ask before leaving unsaved changes (a popup: leave them, as `label` says, or keep editing), with them (and their save
     * buttons) in view behind it: the pricing, else the item number, else the cost, else the supplier price.
     */
    const ask = (leave: () => void, label: string) => {
        void windowConfirm.confirm({ title: "Leave without saving?", message: unsavedNow, confirmLabel: label, cancelLabel: "Keep editing", tone: "danger" }).then(
            (yes) => yes && leave(),
        );
        props.onTab(askTab());
        if (edited) setReveal(nextShowToken());
    };
    // Opening another item here while this one has unsaved changes: it stays, flashes (windows.ts), and asks.
    useUnsavedGuard(kind, unsaved, (proceed) => ask(proceed, `Discard and open the other ${word}`));

    if (!item) {
        return (
            <RecordWindow kind={kind} icon={kind === "product" ? <Puzzle className="size-4" /> : <Package className="size-4" />} title={kind === "product" ? "Product not found" : "Item not found"} tabs={[]} tab="" onTab={() => undefined} onClose={props.onClose}>
                <Notice>{kind === "product" ? "This product doesn't exist any more." : "This item doesn't exist any more (it may have been deleted in ServiceM8)."}</Notice>
            </RecordWindow>
        );
    }
    // What needs attention on it (attention.ts): which warnings its box shows.
    const ctx = itemContext(props.data);
    const found = itemProblems(item, ctx);
    const problems = new Set(found.map((p) => p.id));
    const has = (id: ItemProblemId) => problems.has(id);
    const pricesDue = found.find((p) => p.id === "pricesDue");
    const pricesDueCount = ctx.pricesDue.get(item.uuid);
    // Its pill red when it's a red problem (attention.ts: it trips up accounting software or loses money), as its list's mark is.
    const toneOf = (...ids: ItemProblemId[]) => (found.some((p) => ids.includes(p.id) && p.danger) ? "error" : "warning");
    // The items or bundles sharing its item number, if any.
    const numberClash = ctx.numbers.clashes.get(item.uuid);
    // The other items with its name, if any.
    const nameClash = ctx.names.get(item.uuid);
    // A product's items' own warnings (UsedIn.tsx).
    const builtWarned = builtItemProblems(props.data, item);
    const canEdit = can(tabData, "items.edit");
    const tabs = itemTabs(props.data, item, isPackage);
    const tab = tabs.some((t) => t.id === props.tab) ? props.tab : "details";
    // Show, for a warning put right on a tab this person has (none: just the line).
    const show = (spot: FixSpot) =>
        tabs.some((t) => t.id === FIX_TABS[spot])
            ? () => {
                  props.onTab(FIX_TABS[spot]);
                  setFix({ spot, token: nextShowToken() });
              }
            : undefined;
    const setState = (state: ItemState) => {
        setArchiveError(null);
        saver.setItemState(item.uuid, state).catch((err: Error) => setArchiveError(err));
    };
    const inBundles = props.data.bundles.filter((b) => b.active && b.material_list.some((l) => l.uuid === item.uuid)).length;
    const unmakePackage = () =>
        void windowConfirm.confirm({
            title: "Unlink it from the Catalogue?",
            message: "Its products no longer price it. Its price and cost stay as they are now, typed in.",
            confirmLabel: "Unlink",
        })
            .then((yes) => (yes ? saver.unmakePackage(item.uuid) : undefined))
            .catch((err: Error) => setArchiveError(err));
    // Other items that are the same one (its item number or name: setup.ts duplicatesOf), and (archived as one) what it was merged into.
    // Of its own kind: a product can share an item's number and name (catalogue.ts).
    const duplicates = duplicatesOf(props.data.materials.filter((m) => isProduct(m) === product), item);
    const mergedInto = props.data.materials.find((m) => m.uuid === props.data.mergedItems[item.uuid]);

    return (
        <RecordWindow kind={kind} tone={product ? "rose" : undefined} icon={product ? <Puzzle className="size-4" /> : <Package className="size-4" />} share
            title={
                <span className="inline-flex max-w-full items-center gap-2">
                    {item.hidden ? <EyeOff className="size-3.5 shrink-0" aria-label="Hidden: not offered on jobs" /> : null}
                    <span className="truncate">{itemName(props.data, item)}</span>
                </span>
            }
            label={itemName(props.data, item)}
            subtitle={[product ? "Product" : isPackage ? "Linked to Catalogue" : "", item.item_number].filter(Boolean).join(" · ") || undefined}
            barExtra={retired ? null : <CheckedPill data={props.data} kind="material" id={item.uuid} onError={setArchiveError} />}
            tabs={tabs.map((t) => ({
                ...t,
                unsaved: tabUnsaved(t.id),
                attention: tabAttention(t.id, found),
                // How many: its photos, or the products it's built from.
                count: t.id === "photos" ? photoCount : t.id === "contents" ? props.data.packages[item.uuid]?.length : undefined,
            }))}
            tab={tab}
            onTab={props.onTab}
            onClose={() => {
                if (unsaved) ask(props.onClose, "Discard and close");
                else props.onClose();
            }}
            actions={[
                can(props.data, "items.add") && duplicateAction(word, () => setDuplicating(true)),
                // A bundle holding it, to add labour (or anything) to: its Contents open, ready. Bundles hold items, not products.
                !product &&
                    can(props.data, "bundles") && {
                        icon: <Boxes className="size-4" />,
                        label: "Make a bundle",
                        help: "Sell it with other items, each on its own line.",
                        run: () => setBundling(true),
                    },
                // Built from products: its price and cost theirs, it still one line on jobs (packages.ts). Back to a plain item, asked first.
                !product &&
                    can(props.data, "items.pricing") &&
                    !retired &&
                    (isPackage
                        ? { icon: <Package className="size-4" />, label: "Unlink from Catalogue", help: "Its products no longer price it: its price and cost stay, typed in.", run: unmakePackage }
                        : { icon: <PackageCheck className="size-4" />, label: "Link to Catalogue", help: "Its price and cost come from products in the Catalogue (a new one made from it, plus labour or others).", run: () => setPackaging(true) }),
                // A product sold as it is: a new item in ServiceM8 built from it.
                product &&
                    !retired &&
                    can(props.data, "items.add") &&
                    can(props.data, "items.pricing") && {
                        icon: <Package className="size-4" />,
                        label: "Sell it as an item",
                        help: "A new item in ServiceM8, linked to it.",
                        run: () => setSelling(true),
                    },
                ...(can(props.data, "items.archive") ? stateActions(itemName(props.data, item), itemState(item), inBundles, setState, product) : []),
            ]}
            notices={
                <>
                    <windowConfirm.Here />
                    <ErrorNotice error={archiveError} />
                    {item.hidden ? (
                        <Notice tone={inBundles ? "warning" : "info"}>
                            Hidden: not offered on jobs (archived in ServiceM8), kept priced here. Unhide it (More) when it&apos;s needed.
                            {inBundles ? ` ${plural(inBundles, "bundle")} still ${inBundles === 1 ? "holds" : "hold"} it: using one puts it back on jobs, so take it out.` : ""}
                        </Notice>
                    ) : null}
                    {/* ServiceM8's Smart Labour: anything named Labour is an hourly rate there, wherever it's used (pricing.ts isLabour). */}
                    {isLabour(item) && !product ? <Notice>Labour rate: ServiceM8 charges it by the hour when billing time, as its name says Labour.</Notice> : null}
                    {/* A plain item (its price typed): on every tab, the way to have it worked out from products' costs instead. */}
                    {!product && !isPackage && !retired && can(props.data, "items.pricing") ? (
                        <Notice
                            actions={
                                <button type="button" className="btn-secondary" onClick={() => setPackaging(true)}>
                                    Link to Catalogue
                                </button>
                            }
                        >
                            Typed price. Link it to products to work it out from their costs.
                        </Notice>
                    ) : null}
                    {retired ? (
                        <Notice
                            tone="warning"
                            actions={
                                can(props.data, "items.archive") ? (
                                    <button type="button" className="btn-secondary" onClick={() => setState("sale")}>
                                        Restore
                                    </button>
                                ) : null
                            }
                        >
                            {product ? "This product is archived: items can't be linked to it. Restore it to make changes." : "This item is archived: it can't be added to jobs. Restore it to make changes."}
                        </Notice>
                    ) : null}
                    {/* Its problems as one box, worst first (attention.ts: the same as its list row and Home): a pill each, taking you to where
                        it's put right (the field, or the whole warning with its buttons on its tab). Unknown has no such place: a line with its buttons. */}
                    <Issues
                        chips={
                            <>
                                {/* Changes not saved yet, once you've moved off their tab and until saved or cancelled (windows.ts useLeftUnsaved),
                                    first: each takes you back to its save bar (or Save button). While unsaved they stand in for the warning saving resolves. */}
                                {left.has("pricing") ? <IssueChip tone="unsaved" label="Unsaved pricing" title="Its pricing has changes: save or cancel them." onShow={show("pricing")} /> : null}
                                {left.has("details") ? <IssueChip tone="unsaved" label="Unsaved details" title="Its name, item number, barcode or stock has changes: save or cancel them." onShow={() => props.onTab("details")} /> : null}
                                {left.has("cost") ? <IssueChip tone="unsaved" label="Unsaved cost" title="Its cost has changes: save or cancel them." onShow={() => props.onTab("suppliers")} /> : null}
                                {left.has("parts") ? <IssueChip tone="unsaved" label="Unsaved products" title="What it's built from has changes: save or cancel them." onShow={() => props.onTab("contents")} /> : null}
                                {left.has("tags") ? <IssueChip tone="unsaved" label="Unsaved tags" title="Its tags have changes: save or cancel them." onShow={show("name")} /> : null}
                                {left.has("price") ? <IssueChip tone="unsaved" label="Unsaved supplier price" title="A supplier price is being added: add it or cancel it." onShow={() => props.onTab("suppliers")} /> : null}
                                {(has("noNumber") || has("sharedNumber")) && details?.item_number === undefined ? (
                                    <IssueChip tone={toneOf("noNumber", "sharedNumber")} label={numberClash ? "Shared item number" : "No item number"} title={numberClash ? sharedNumberText(numberClash, "item") : "Every item needs one."} onShow={() => goTo("itemNumber")} />
                                ) : null}
                                {nameClash ? <IssueChip label="Shared name" title={sharedNameText(nameClash)} onShow={() => goTo("name")} /> : null}
                                {has("low") ? <IssueChip tone={toneOf("low")} label={profitTitle(props.data, item)} onShow={show("profit")} /> : null}
                                {has("servicem8Price") ? <IssueChip tone={toneOf("servicem8Price")} label={found.find((p) => p.id === "servicem8Price")?.text ?? "Priced by ServiceM8"} onShow={show("pricing")} /> : null}
                                {/* What was changed there is what it is: its History tab says what, to be seen (or put back). */}
                                {has("changed") ? <IssueChip label="Changed in ServiceM8" onShow={show("history")} /> : null}
                                {has("check") && !edited ? <IssueChip tone={toneOf("check")} label={found.find((p) => p.id === "check")?.text ?? "Price to update"} onShow={show("pricing")} /> : null}
                                {has("costUpdate") ? <IssueChip label="Cost to update" onShow={show("cost")} /> : null}
                                {/* A product's items with warnings of their own (their names to update...): marked in its Used in. */}
                                {builtWarned.size ? (
                                    <IssueChip
                                        tone={[...builtWarned.values()].some((list) => list.some((p) => p.danger)) ? "error" : "warning"}
                                        label={`${plural(builtWarned.size, "item")} ${builtWarned.size === 1 ? "needs" : "need"} attention`}
                                        title="Items linked to it have warnings of their own: open each from its Used in."
                                        onShow={show("usedIn")}
                                    />
                                ) : null}
                                {has("itemsToUpdate") ? <IssueChip label={found.find((p) => p.id === "itemsToUpdate")?.text ?? "Items to update"} title="Items linked to it aren't what their products add up to now." onShow={show("usedIn")} /> : null}
                                {/* Its supplier prices to check: on its Suppliers tab (each one's Checked, once it's still right). */}
                                {pricesDue && pricesDueCount ? <IssueChip label={pricesDue.text} title={pricesDueDetail(pricesDueCount)} onShow={show("prices")} /> : null}
                                {has("noCost") ? <IssueChip label="No cost" title="Its pricing works from the cost, so it can't work out a price without one" onShow={can(props.data, "costs.view") ? () => goTo("cost") : undefined} /> : null}
                                {has("names") ? <IssueChip label="Name to update" onShow={show("name")} /> : null}
                                {has("tagClash") ? (
                                    <IssueChip label={found.find((p) => p.id === "tagClash")?.text ?? "Tag clash"} title="Its set allows only one of its tags: take the others off." onShow={show("name")} />
                                ) : null}
                            </>
                        }
                    >
                        {/* Nowhere else to be put right: its buttons here. */}
                        {has("unknown") ? <UnknownNotice data={props.data} item={item} onArchive={() => setState("retired")} /> : null}
                        {/* Another item that's the same one (its item number or name): merged into one (merge.ts). */}
                        {duplicates.length && can(props.data, "items.archive") && !product ? (
                            <Notice
                                tone="warning"
                                title="Duplicate."
                                actions={
                                    <button type="button" className="btn-secondary" onClick={() => setMerging(true)}>
                                        Merge...
                                    </button>
                                }
                            >
                                {duplicates.map((d) => itemName(props.data, d)).join(", ")} {duplicates.length === 1 ? "has" : "have"} its item number or name. Merge {duplicates.length === 1 ? "them" : "one"} into one item.
                            </Notice>
                        ) : null}
                    </Issues>
                    {/* Merged into another as a duplicate (merge.ts): that one's the item now. */}
                    {mergedInto ? (
                        <Notice
                            actions={
                                <button type="button" className="btn-secondary" onClick={() => windows.openItem(mergedInto.uuid)}>
                                    Open it
                                </button>
                            }
                        >
                            Merged into {itemName(props.data, mergedInto)}, a duplicate of it.
                        </Notice>
                    ) : null}
                    {/* Why this tab can't be changed, if it can't (its own permission). */}
                    {whyNot(props.data, tabAction(tab)) ? <Notice>{whyNot(props.data, tabAction(tab))}</Notice> : null}
                </>
            }
        >
            {/* Its tab fills the window's height, so what sits at a tab's end (mt-auto: what it makes per unit) can sit at the bottom. */}
            <div className="flex flex-1 flex-col">
                {tab === "details" && (
                    <DetailsTab
                        data={tabData}
                        item={item}
                        product={product}
                        canEdit={canEdit}
                        canEditNumber={can(props.data, "items.edit")}
                        itemNumberFocus={focusOn("itemNumber")}
                        nameFocus={focusOn("name")}
                        nameShared={nameClash ? sharedNameText(nameClash) : undefined}
                        detailsDraft={details}
                        onDetailsDraft={setDetailsDraft}
                        tagsDraft={tagsDraft}
                        onTagsDraft={setTagsDraft}
                        // Its name to update (under its name); its tags' clash (with its tags).
                        nameNotice={has("names") ? <SmartNameNotice data={props.data} item={item} /> : undefined}
                        tagsFix={has("tagClash") ? <TagClashNotice text={found.find((p) => p.id === "tagClash")?.text ?? ""} /> : undefined}
                        nameFixToken={fixToken("name")}
                        usedInToken={fixToken("usedIn")}
                    />
                )}
                {/* Each tab's warnings in full, with their buttons, by what they're about: where a warning's Show at the top brings you. */}
                {tab === "pricing" && (
                    <PricingTab
                        data={tabData}
                        item={item}
                        draft={draft}
                        onDraft={setDraft}
                        reveal={reveal}
                        onCostTab={() => goTo("cost")}
                        onPartsTab={() => props.onTab("contents")}
                        costDraft={costDraft}
                        profitToken={fixToken("profit")}
                        fix={
                            // Its own pricing being edited: not the price check too (its save bar's Save & update price says what saving does to it).
                            has("check") && !edited ? <CheckNotice data={props.data} item={item} /> : undefined
                        }
                        fixToken={fixToken("pricing")}
                    />
                )}
                {/* Its Costing tab: its cost, and what it's costed from (its supplier prices). */}
                {tab === "suppliers" ? (
                    <SupplierPricesTab
                        pricingDraft={draft}
                        data={tabData}
                        item={item}
                        costFocus={focusOn("cost")}
                        priceDraft={priceDraft}
                        onPriceDraft={setPriceDraft}
                        costDraft={costDraft}
                        onCostDraft={setCostDraft}
                        costFix={has("costUpdate") ? <CostUpdateNotice data={props.data} item={item} /> : undefined}
                        costFixToken={fixToken("cost")}
                        pricesFixToken={fixToken("prices")}
                    />
                ) : null}
                {tab === "contents" && <PartsTab data={tabData} item={item} draft={partsDraft} onDraft={setPartsDraft} onOpenItem={(uuid) => windows.openItem(uuid)} />}
                {tab === "stats" && <ItemStatsTab uuid={item.uuid} />}
                {tab === "photos" && <PhotosTab data={tabData} kind="material" id={item.uuid} />}
                {tab === "history" && (
                    <>
                        <FixHere token={fixToken("history")} className={FIX_TOP}>
                            {has("changed") ? <ChangedNotice object="material" uuid={item.uuid} data={props.data} /> : null}
                        </FixHere>
                        <HistoryTab object="material" uuid={item.uuid} data={props.data} />
                    </>
                )}
                <LeaveGuard window={[kind, item.uuid]} when={unsaved} message={`${itemName(props.data, item)}: ${unsavedNow}`} />
                {packaging ? (
                    <BuildPopup
                        data={props.data}
                        item={item}
                        onClose={() => setPackaging(false)}
                        onMade={() => {
                            setPackaging(false);
                            props.onTab("contents");
                        }}
                    />
                ) : null}
                {bundling ? (
                    <DuplicatePopup
                        data={props.data}
                        kind="bundle"
                        title="Make a bundle"
                        createLabel="Make bundle"
                        intro="A bundle holding this item. Add more items on its Contents: each goes on jobs as its own line."
                        name={item.name}
                        number={freeNumber(props.data, `${item.item_number.trim() || "ITEM"}-KIT`)}
                        withNumber
                        supplierPrices={null}
                        // Its tags too: a bundle has its own, starting as the item's.
                        onCreate={(b) =>
                            saver
                                .createBundle({ name: b.name, item_number: b.itemNumber, material_list: [{ uuid: item.uuid, quantity: 1 }], tagIds: props.data.itemTags[item.uuid] ?? [], uuid: newUuid.get() })
                                .then((made) => {
                                    newUuid.made();
                                    windows.openBundle(made.uuid, "contents");
                                })
                        }
                        onClose={() => setBundling(false)}
                    />
                ) : null}
                {merging && duplicates.length ? (
                    <MergePopup
                        data={props.data}
                        item={item}
                        duplicates={duplicates}
                        onClose={() => setMerging(false)}
                        onMerged={(kept) => {
                            setMerging(false);
                            if (kept !== item.uuid) windows.openItem(kept);
                        }}
                    />
                ) : null}
                {selling ? (
                    <DuplicatePopup
                        data={props.data}
                        kind="item"
                        title="Sell it as an item"
                        createLabel="Make item"
                        intro="A new item in ServiceM8, linked to this product: its price and cost come from it."
                        name={item.name}
                        number={freeNumber(props.data, item.item_number.trim() || "ITEM")}
                        withNumber
                        supplierPrices={null}
                        onCreate={(made) =>
                            saver.makePackage({ create: { name: made.name, item_number: made.itemNumber } }, [{ uuid: item.uuid, quantity: 1 }]).then((m) => windows.openItem(m.uuid, "contents"))
                        }
                        onClose={() => setSelling(false)}
                    />
                ) : null}
                {duplicating ? (
                    <DuplicatePopup
                        data={props.data}
                        kind={product ? "product" : "item"}
                        name={item.name}
                        withNumber
                        // Its supplier prices can come too (adding them needs that permission).
                        supplierPrices={can(props.data, "prices.add") ? props.data.supplierPrices.filter((p) => p.materialUuid === item.uuid).length || null : null}
                        // A built item's copy is built too, from the same products.
                        onCreate={(copy) =>
                            saver
                                .duplicateItem(item.uuid, { ...copy, uuid: newUuid.get() })
                                .then(async (made) => {
                                    newUuid.made();
                                    const parts = props.data.packages[item.uuid];
                                    if (parts) await saver.makePackage({ itemUuid: made.uuid }, parts);
                                    return made;
                                })
                                .then((made) => windows.openItem(made.uuid))
                        }
                        onClose={() => setDuplicating(false)}
                    />
                ) : null}
            </div>
        </RecordWindow>
    );
}

/** Where each problem is put right (its tab), for marking the tab: the same places its Needs attention pills take you. */
const PROBLEM_TABS: Partial<Record<ItemProblemId, string>> = {
    noNumber: "details",
    sharedNumber: "details",
    sharedName: "details",
    unknown: "details",
    names: "details",
    tagClash: "details",
    servicem8Price: "pricing",
    itemsToUpdate: "details",
    check: "pricing",
    low: "pricing",
    costUpdate: "suppliers",
    pricesDue: "suppliers",
    noCost: "suppliers",
    changed: "history",
};

/** A tab's mark for what on it needs attention ("!", red when any of it is wrong), saying what (on hover); none: nothing. */
function tabAttention(tab: string, found: readonly { id: ItemProblemId; text: string; danger: boolean }[]) {
    const here = found.filter((p) => PROBLEM_TABS[p.id] === tab);
    return here.length ? { danger: here.some((p) => p.danger), label: here.map((p) => p.text).join(", ") } : undefined;
}

/** A tab's warnings in full, as a section of it: lined up with the rest. */
const FIX_TOP = "space-y-2 border-b border-line px-4 py-3";

/**
 * No one has said it's a real item yet (known.ts): it was added in ServiceM8, not here, e.g. a part
 * number typed on a job. At the top of its window (only for unknown items) until someone keeps it
 * (known) or archives it (`onArchive`, as the More menu's Archive, asked first; restored, it's unknown again).
 */
function UnknownNotice({ data, item, onArchive }: Readonly<{ data: PriceListData; item: MaterialRow; onArchive: () => void }>) {
    const confirm = useConfirm();
    const saver = usePriceListSaver();
    const [error, setError] = useState<Error | null>(null);
    return (
        <>
            <Notice
                tone="warning"
                title="Unknown item from ServiceM8."
                actions={
                    can(data, "items.review") ? (
                        <>
                            <button type="button" className="btn-secondary" onClick={() => saver.markKnown([item.uuid]).catch((err: Error) => setError(err))}>
                                Keep
                            </button>
                            <button
                                type="button"
                                className="btn-secondary"
                                onClick={async () => {
                                    const archive = archiveAction("item", itemName(data, item), true, onArchive);
                                    if (!archive.confirm || (await confirm({ ...archive.confirm, tone: "danger" }))) archive.run();
                                }}
                            >
                                Archive
                            </button>
                        </>
                    ) : null
                }
            >
                Please review it, then keep or archive it.{" "}
                <span className="mt-0.5 block text-xs opacity-80">Unknown items can come from part numbers on jobs that don&apos;t match an item, or from your accounting software once invoices are approved.</span>
            </Notice>
            <ErrorNotice error={error} />
        </>
    );
}
